import { Fragment } from 'react/jsx-runtime';
import styled from 'styled-components';
import { Button } from '../Button';
import type { PricingCardProps } from './index.type';

const Heading = styled.h2`
  * {
    overflow-wrap: break-word;
  }
  max-width: 100%;
  margin: 0 0 1rem 0;
  font-size: 2rem;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -1px;
  min-width: 0;
`;

const FeaturedPill = styled.aside`
  color: #FF4473;
  text-align: center;
  flex-shrink: 0;
  font-weight: 500;
  line-height: 1;
  border-radius: 100px;
  background: #FFDCE5;
  padding: 0.375rem 0.75rem;
`;

const DesktopFeatures = styled.div`
`;

const MobileFeatures = styled.details`
  &[open] [data-chevrondown] {
    transform: rotate(180deg);
  }
  summary {
    display: flex;
    user-select: none;
    list-style: none;
    gap: 0.5rem;
    cursor: pointer;
    align-items: center;
    justify-content: space-between;
    ::-webkit-details-marker {
      display: none;
    }
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  margin-bottom: 2.5rem;
  > * {
    flex: 1;
  }
`;

const Price = styled.div`
  margin-bottom: 2rem;
`;

const HeadingWrapper = styled.div`
  display: flex;
  flex-wrap: wrap-reverse;
  justify-content: space-between;
  align-items: start;
  gap: 0.25rem 0.5rem;
`;

const ListHeading = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
  font-weight: 600;
`;

const TopAlignment = styled.div<{ $minWidthTrigger?: number }>`
  @media screen and (min-width: ${props => props.$minWidthTrigger}px) {
    min-height: 18rem;
  }
`;

const Description = styled.div`
  margin: 0 0 1.5rem 0;
  font-size: 1rem;

  p + p {
    margin-top: 0.5rem;
  }

  ul {
    list-style-position: inside;
    padding: 0 0 0 0.5rem;
    margin: 0;
    li span {
      position: relative;
      left: -0.5rem;
    }
  }
`;

const UspList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  &:not(:last-child) {
    margin-bottom: 1.5rem;
  }

  li {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    &:not(:last-child) {
      margin-bottom: 0.5rem;
    }
  }
`;

const FromText = styled.div`
  color:  #ACACBB;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.2;
`;

const PriceText = styled.span`
  font-size: 1.375rem;
  font-weight: 500;
  line-height: 1.20;
  letter-spacing: -0.66px;
`;
const PeriodText = styled.span`
  color: #686878;
`;

const PricingCardContent = styled.section<{
  $featured: boolean;
  $breakpointForSingleColumn?: number;
}>`
  background: white;
  display: flex;
  flex-direction: column;
  color: #181646;
  border-radius: 2rem;
  border: 0.5rem solid ${props => (props.$featured ? '#FF4473' : 'transparent')}; // todo use token
  padding: 2.5rem;
  min-width: 22.5rem;
  max-width: 28.75rem;
  > * {
    max-width: 100%;
  }

  ${MobileFeatures} {
    display: none;
  }
  @media screen and (max-width: ${props => props.$breakpointForSingleColumn}px) {
    padding: 1.5rem;
    ${DesktopFeatures} {
      display: none;
    }
    ${MobileFeatures} {
      display: block;
    }

    ${Heading} {
      margin-bottom: 0.5rem;
      font-size: 1.5rem;
    }
    ${Price} {
      margin-bottom: 1.5rem;
    }
    ${Description} {
      margin-bottom: 1rem;
    }
    ${ButtonWrapper} {
      margin-bottom: 2rem;
    }
    ${PriceText} {
      font-size: 1.125rem;
    }
    ${PeriodText} {
      font-size: 0.875rem;
    }
  }
`;

const CheckIcon = () => (
  <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.166016" width="20" height="20" rx="10" fill="#181646" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14.5196 6.64645C14.7148 6.84171 14.7148 7.15829 14.5196 7.35355L9.01957 12.8536C8.82431 13.0488 8.50772 13.0488 8.31246 12.8536L5.81246 10.3536C5.6172 10.1583 5.6172 9.84171 5.81246 9.64645C6.00772 9.45118 6.32431 9.45118 6.51957 9.64645L8.66602 11.7929L13.8125 6.64645C14.0077 6.45118 14.3243 6.45118 14.5196 6.64645Z"
      fill="white"
    />
  </svg>
);

const PlusIcon = () => (
  <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.666016" y="0.5" width="19" height="19" rx="9.5" stroke="#181646" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.16602 10C6.16602 9.72386 6.38987 9.5 6.66602 9.5H13.666C13.9422 9.5 14.166 9.72386 14.166 10C14.166 10.2761 13.9422 10.5 13.666 10.5H6.66602C6.38987 10.5 6.16602 10.2761 6.16602 10Z"
      fill="#181646"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10.166 14C9.88987 14 9.66602 13.7761 9.66602 13.5L9.66602 6.5C9.66602 6.22386 9.88987 6 10.166 6C10.4422 6 10.666 6.22386 10.666 6.5L10.666 13.5C10.666 13.7761 10.4422 14 10.166 14Z"
      fill="#181646"
    />
  </svg>
);

const ArrowIcon = () => (
  <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.666016" y="0.5" width="19" height="19" rx="9.5" stroke="#181646" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.81246 6.14645C10.0077 5.95118 10.3243 5.95118 10.5196 6.14645L14.0196 9.64645C14.2148 9.84171 14.2148 10.1583 14.0196 10.3536L10.5196 13.8536C10.3243 14.0488 10.0077 14.0488 9.81246 13.8536C9.6172 13.6583 9.6172 13.3417 9.81246 13.1464L12.4589 10.5H6.66602C6.38987 10.5 6.16602 10.2761 6.16602 10C6.16602 9.72386 6.38987 9.5 6.66602 9.5H12.4589L9.81246 6.85355C9.6172 6.65829 9.6172 6.34171 9.81246 6.14645Z"
      fill="#181646"
    />
  </svg>
);

const PricingCard = (props: PricingCardProps) => {
  const featuresHeadingLevel = props.headingLevel === 'h3' ? 'h4' : 'h5';
  const featuresContent = props.features.map((featureGroup, i) => {
    const icon =
      featureGroup.icon === 'CHECK' ? (
        <CheckIcon />
      ) : featureGroup.icon === 'ARROW' ? (
        <ArrowIcon />
      ) : featureGroup.icon === 'PLUS' ? (
        <PlusIcon />
      ) : null;
    return (
      <Fragment key={i}>
        {i > 0 && <ListHeading as={featuresHeadingLevel}>{featureGroup.heading}</ListHeading>}
        <UspList>
          {featureGroup.usps.map((usp, i) => (
            <li key={i}>
              {icon}
              <span>{usp}</span>
            </li>
          ))}
        </UspList>
      </Fragment>
    );
  });

  return (
    <PricingCardContent
      $featured={props.featured}
      $breakpointForSingleColumn={props.breakpointForSingleColumn}
    >
      <TopAlignment $minWidthTrigger={props.breakpointForSingleColumn}>
        <HeadingWrapper>
          <Heading as={props.headingLevel}>
            <div>{props.title}</div>
            {props.titleRow2}
          </Heading>
          {props.featuredPillText && props.featured ? (
            <FeaturedPill>{props.featuredPillText}</FeaturedPill>
          ) : null}
        </HeadingWrapper>
        <Price>
          {props.price.fromText ? <FromText>{props.price.fromText}</FromText> : null}
          <PriceText>{props.price.amount}</PriceText>
          {props.price.period ? <PeriodText>{props.price.period}</PeriodText> : null}
        </Price>
        <Description
          dangerouslySetInnerHTML={{
            __html: props.descriptionHtml || '',
          }}
        />
      </TopAlignment>
      <ButtonWrapper>
        <Button configuration={props.featured ? 'primary' : 'secondary'} href={props.cta.url}>
          {props.cta.text}
        </Button>
      </ButtonWrapper>
      {props.features.length > 0 ? (
        <>
          <MobileFeatures>
            <summary>
              <ListHeading as={featuresHeadingLevel}>{props.features[0].heading}</ListHeading>
              <svg
                data-chevrondown="1"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M4.41009 6.91083C4.73553 6.58539 5.26317 6.58539 5.5886 6.91083L9.99935 11.3216L14.4101 6.91083C14.7355 6.58539 15.2632 6.58539 15.5886 6.91083C15.914 7.23626 15.914 7.7639 15.5886 8.08934L10.5886 13.0893C10.2632 13.4148 9.73553 13.4148 9.41009 13.0893L4.41009 8.08934C4.08466 7.7639 4.08466 7.23626 4.41009 6.91083Z"
                  fill="currentColor"
                />
              </svg>
            </summary>
            {featuresContent}
          </MobileFeatures>
          <DesktopFeatures>
            <ListHeading as={featuresHeadingLevel}>{props.features[0].heading}</ListHeading>
            {featuresContent}
          </DesktopFeatures>
        </>
      ) : null}
    </PricingCardContent>
  );
};

export default PricingCard;
